import { formatPercent } from './numberFormat'
/**
 * WealthOS / MyCIO design tokens (warm cream + forest green). Shared across the MyCIO routes.
 * Exact values from the website designer's handoff (aicio/sample website #4/design_handoff_mycio/README.md).
 * Existing short keys (green/greenDark/…) are kept as aliases so current components don't break; the
 * handoff-named tokens (forest/forestDeep/sage/…) are the canonical set for the faithful rebuild.
 */
export const MC = {
  // backgrounds / surfaces
  bg: '#F6F3EC', // page background (warm bone) — handoff `bg`
  cream: '#F6F3EC',
  surface: '#FCFAF4', // cards, inputs, raised surfaces
  paper: '#FCFAF4',
  surfaceAlt: '#F1ECE0', // inset chips, code blocks, open accordion head
  // ink / text
  ink: '#23211C',
  inkSoft: '#4A463D',
  ink2: '#4A463D',
  muted: '#5C574C',
  muted2: '#6B6557', // labels
  faint: '#8A8475', // captions, meta
  faint2: '#A9A192', // placeholders, disabled
  // borders
  border: '#DED7C7',
  borderSoft: '#E8E0CF',
  // brand
  forest: '#2F5D4F',
  green: '#2F5D4F',
  forestDeep: '#21463A',
  greenDark: '#21463A',
  sage: '#9DBDAE',
  greenSoft: '#9DBDAE',
  // Chart-safe sage. `sage` (#9DBDAE) measures 1.95:1 on the #FCFAF4 chart surface — below the 3:1
  // a data mark needs — so an upside STROKE drawn in it washes out (dataviz validator, 2026-09-11).
  // This is the nearest darker step that passes contrast while staying >=15 ΔE from `forest` for
  // normal vision; `sage` itself remains correct for FILLS and tints, which carry no such floor.
  sageDeep: '#5E8F78',
  sageBg: '#E6EDE7', // success/positive tint
  pillBg: '#E6EDE7',
  mist: '#B7CABF',
  gold: '#C9B79B', // serif numerals, editorial accent
  clay: '#A85636', // "At risk" / warning text
  clayBg: '#F2E4DB', // warning callout bg
  amberBannerBg: '#3A2E16', // "Acting as analyst" banner bg (text amberText)
  amberBg: '#3A2E16',
  amberText: '#F3E7CE',
} as const

/** Font-family stacks. These resolve through the CSS variables that `next/font/google`
 * (in app/mycio/layout.tsx) sets on `.mycio-root`, with literal-name + system fallbacks so the
 * faithful inline styles render correctly even before the font swaps in. */
export const SERIF = "var(--font-newsreader), 'Newsreader', Georgia, 'Times New Roman', serif"
export const SANS = "var(--font-hanken), 'Hanken Grotesk', system-ui, -apple-system, sans-serif"

/** Standard easing + the hero cross-dissolve timings, from the handoff Motion section. */
export const MOTION = {
  ease: 'cubic-bezier(0.22,1,0.36,1)',
  heroHoldMs: 6000,
  heroFadeOutMs: 740,
  heroDissolveMs: 1500,
} as const

/** Band → chip colors, matching the engine's Secure / On track / At risk / Off track semantics. */
export function bandColors(band?: string): { dot: string; text: string; bg: string } {
  const b = (band || '').toLowerCase()
  // Band semantics — exact from the handoff (Secure / On track / At risk). Off-track keeps a deeper clay.
  if (b.includes('secure')) return { dot: '#21463A', text: '#21463A', bg: '#DDE8E0' }
  if (b.includes('on track')) return { dot: '#2F5D4F', text: '#2F5D4F', bg: '#E6EDE7' }
  if (b.includes('at risk') || b.includes('watch')) return { dot: '#A85636', text: '#A85636', bg: '#F2E4DB' }
  if (b.includes('off track')) return { dot: '#8C4A2E', text: '#8C4A2E', bg: '#F2E4DB' }
  return { dot: '#8A8475', text: '#6B6557', bg: '#EEE8DC' }
}

// Show up to 1 decimal, trimmed: 0.025 -> "2.5%" (NOT "3%"), 0.0717 -> "7.2%", 0.9 -> "90%", 0.23 -> "23%".
// Math.round(n*100) wrongly turned a 2.5% inflation assumption into "3%".
// Notation follows the member's language (de "2,5 %", fr "2,5 %", tr "%2,5") — see numberFormat.ts.
// `locale` is the member's number locale (useNumberLocale()); English when omitted.
export const fmtPct = (n?: number, locale?: string) => formatPercent(n, { maximumFractionDigits: 1, locale })
